import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { CardLayout, MTable } from "../v3";
import { AccountTableColumns } from "../../pages/account/constants";
import { AccountService } from "../../services";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "../../utils";
import { VIEW_ACCOUNT } from "../../constants";
import { handlePdfDownload, Listing } from "../pdf";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";

export const AccountList = ({ companyId = null, reloadData, totalCount = 0, setTotalCount = () => {} }) => {
  const [accounts, setAccounts] = useState([]);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    searchText,
    setSearchText,
    sortModel,
    setSortModel,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
  } = usePersistedTableState({
    tableId: "accounts",
    defaults: { sortModel: [{ field: "updatedAt", sort: "desc" }] },
  });
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);
  const { ownerUserId: companyAdminId } = user.company;

  const hasViewAccount = useHasPermission(VIEW_ACCOUNT);

  const fetchData = useCallback(
    async (accountFilters = {}) => {
      setLoading(true);
      try {
        if (companyId !== null) {
          const { data: accountResponse } = await AccountService.getAccounts({ ...accountFilters, companyId });
          setAccounts(accountResponse?.rows);
          setTotalCount(accountResponse?.count || accountResponse?.rows?.length);
        } else {
          const { data: accountResponse } = await AccountService.getAdminAndUserAccounts({
            hasViewAccount,
            user,
            companyAdminId,
            ...accountFilters,
          });
          setAccounts(accountResponse?.rows);
          setTotalCount(accountResponse?.count || accountResponse?.rows?.length);
        }
      } catch (error) {
        toast.error("Failed to fetch accounts");
        console.error("🚀 ~ fetchData ~ error:", error);
      } finally {
        setLoading(false);
      }
    },
    [user, companyAdminId, hasViewAccount, setTotalCount, companyId],
  );

  useEffect(() => {
    fetchData({
      sortBy: sortModel[0]?.field,
      sortOrder: sortModel[0]?.sort,
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      name: searchText,
    });
  }, [fetchData, sortModel, paginationState, searchText, reloadData]);

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfColumnHeaders = derivePdfColumns(AccountTableColumns);
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing title="Accounts" columnHeaders={pdfColumnHeaders} tableRows={printableRows} company={user?.company} />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: "Accounts",
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const onViewClick = (params, event) => {
    navigate(`/account/${params.row.id}`);
  };

  const rows = accounts
    ? accounts.map((account, index) => ({
        id: account.id,
        sl: index + 1 + paginationState.page * paginationState.pageSize,
        name: account.name,
        type: account.type,
        balance: Number(account.balance),
      }))
    : [];

  return (
    <CardLayout>
      <MTable
        // Search data
        placeholder="Search account"
        searchText={searchText}
        onSearchChange={handleSearchChange}
        showSearchBar
        // Export data
        showExportButton
        isPdfGenerating={isPdfGenerating}
        onExportPdfClick={handleDownloadPdfClick}
        // Table data
        columnHeaders={AccountTableColumns}
        tableRows={rows}
        rowCount={totalCount || 0}
        loading={loading}
        sortModel={sortModel}
        onSortChange={setSortModel}
        paginationModel={paginationState}
        onPaginationChange={handlePaginationChange}
        onRowClick={onViewClick}
      />
    </CardLayout>
  );
};
